import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { track } from "../utils/analytics";
import { processCertificateData } from "../utils/certificate";
import { generateCertificatePDF } from "../utils/pdf";
import CertificatePrint from "./CertificatePrint";
import Button from "./ui/Button";

export default function CertificateDetail({ cert, certificateIndex = 0, onClose, printDate }) {
  const { orderId } = useParams();
  const certificateRef = useRef(null);
  const printCertificateRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Track certificate view on mount
  useEffect(() => {
    track("Certificate Viewed", { order_id: orderId, certificate_index: certificateIndex });
  }, [orderId, certificateIndex]);

  const handleClose = () => {
    track("Certificate Closed", { order_id: orderId, certificate_index: certificateIndex });
    onClose();
  };

  // Disable body scrolling when overlay is open
  useEffect(() => {
    // Store the original overflow value
    const originalOverflow = document.body.style.overflow;
    // Disable scrolling
    document.body.style.overflow = "hidden";

    // Re-enable scrolling when component unmounts
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Process certificate data using shared utility
  const {
    cert: processedCert,
    formattedPrintDate,
    productTitleForUrl,
    qrUrl,
  } = processCertificateData(cert, printDate);

  const handleDownload = async () => {
    if (!printCertificateRef.current) {
      console.error("Certificate print element not found");
      return;
    }

    setIsDownloading(true);
    try {
      // Generate PDF from the right certificate details section of CertificatePrint component
      await generateCertificatePDF(printCertificateRef.current, orderId, processedCert);
      track("Certificate Downloaded", { order_id: orderId, certificate_index: certificateIndex });
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const mailtoLink = `mailto:orders@oditi.com?subject=Re: Issue regarding Order %23${orderId} / ${encodeURIComponent(processedCert.title)}`;

  const scaleFactor =
    processedCert.title.toLowerCase() == "this lime green and pleasant land"
      ? "md:scale-[2]"
      : "md:scale-[1.75]";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center md:p-6"
      ref={certificateRef}
    >
      {/* backdrop */}
      <div
        onClick={handleClose}
        className="absolute inset-0 bg-collectors-black/80 backdrop-blur-sm transition-opacity duration-500"
      ></div>

      {/* ticket modal */}
      <div className="certificate-content relative bg-collectors-bg w-full max-w-5xl h-full md:h-[80vh] shadow-2xl animate-slide-up flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
        {/* close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-30 p-2 bg-collectors-black text-white hover:bg-red-600 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* left artwork visual */}
        <div className="w-full md:w-5/12 bg-white p-0 flex items-center justify-center border-solid border-r border-collectors-black md:h-[80vh] md:flex-shrink-0">
          <div className="relative w-full h-full min-h-[300px] md:h-full overflow-hidden">
            <img
              src={processedCert.image}
              alt={processedCert.title}
              className={`absolute inset-0 w-full h-full object-cover grayyscale contrast-125 scale-[3] ${scaleFactor} grayscale-[.25]`}
            />
            <div className="absolute bottom-4 left-4 bg-white px-2 py-1 font-mono text-xs border border-solid border-black">
              FIG. 1 - REF. 123124
            </div>
          </div>
        </div>

        {/* right certificate details */}
        <div className="w-full md:w-7/12 p-4 md:p-8 flex flex-col relative md:overflow-y-auto md:h-[80vh]">
          {/* header */}
          <div className="border-solid border-b-2 border-collectors-black pb-6 mb-8">
            <span className="bg-collectors-black text-white px-2 py-0.5 font-mono text-xs uppercase">
              Certificate of Authenticity
            </span>
            <h1 className="font-fisplay text-4xl md:text-5xl font-bold uppercase leading-[0.9] tracking-tighter mt-4 mb-2">
              {processedCert.title}
            </h1>
            <p className="font-mono text-sm ms-1 uppercase tracking-tight">
              {processedCert.artist}
            </p>
          </div>

          {/* specs */}
          <div className="grid grid-cols-2 gap-8 font-mono text-xs uppercase mb-10">
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Medium
              </span>
              <span className="block font-bold text-sm tracking-tight">
                GICLEE PRINT ON MATTE ARCHIVAL PAPER
              </span>
            </div>
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Dimensions
              </span>
              <span className="block font-bold text-sm tracking-tight">
                {processedCert.variant_title.split("/")[0].replaceAll('"', " inches")}
              </span>
            </div>
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Edition
              </span>
              <span className="block font-bold text-sm tracking-tight">
                {processedCert.edition}
              </span>
            </div>
            <div>
              <span className="block text-collectors-gray mb-1 border-solid border-b border-collectors-gray/30 pb-1">
                Print Date
              </span>
              <span className="block font-bold text-sm tracking-tight">{formattedPrintDate}</span>
            </div>
          </div>

          {/* description */}
          <div className="mb-10">
            <p className="font-sans text-sm leading-relaxed text-collectors-black border-solid border-l-2 border-oditi-black pl-4">
              This document certifies that the work received is an authentic fine art print by an
              ODITI Collaborator and were published by ODITI. All copyright and further reproduction
              rights are retained by the Collaborator.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 font-mono text-xs mb-10 border-solid border-t-2 border-collectors-black pt-8 ps-1">
            {/* artist signature element */}
            <div className="inline-flex flex-col">
              <div className="border-solid border-b border-collectors-gray/30 pb-2 mb-2 text-collectors-gray">
                <span className="block font-medium text-black">{processedCert.artist}</span>
                <span className="block text-xs uppercase tracking-wide">ODITI Collaborator</span>
              </div>
              <span className="mt-3 font-signature text-2xl md:text-3xl font-bold tracking-wider capitalize">
                {processedCert.artist.toLowerCase().replace(/[0-9]/g, "").trim()}
              </span>
            </div>
            {/* harry signature element */}
            <div className="inline-flex flex-col">
              <div className="border-solid border-b border-collectors-gray/30 pb-2 mb-2 text-collectors-gray">
                <span className="block font-medium text-black">Harry Beard</span>
                <span className="block text-xs uppercase tracking-wide">Co-founder at ODITI</span>
              </div>
              <span className="mt-3 font-signature text-2xl md:text-3xl font-bold tracking-wider">
                Harry Beard
              </span>
              {/* <div className="pt-1 pr-4 mx-auto"> 
                <img 
                  className="h-16" 
                  src="https://cdn.shopify.com/s/files/1/0921/0239/4188/files/harry_1.svg?v=1764183136" 
                  alt="Harry Beard signature" 
                />
              </div> */}
            </div>
          </div>

          {/* footer */}
          <div className="mt-auto pt-8 border-solid border-t-2 border-collectors-black grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* left col: qr link */}
            <a
              href={`https://oditi.com/products/${productTitleForUrl}`}
              target="_blank"
              className="flex group hover:bg-collectors-black hover:cursor-pointer border border-solid border-black gap-4 no-underline items-center p-2 md:px-3 bg-white h-full"
            >
              <div className="w-20 h-20 bg-white border-solid border-collectors-black p-1">
                <img
                  src={qrUrl}
                  alt="Product QR Code"
                  className="w-full h-full object-contain mix-blend-multiply"
                />
              </div>
              <div className="flex flex-col gap-1 text-collectors-black">
                <span className="font-mono text-xs uppercase text-collectors-gray group-hover:text-white">
                  View Product
                </span>
                <span className="font-display group-hover:text-white group-hover:underline text-collectors-black font-bold uppercase text-sm tracking-tight">
                  oditi.com ↗
                </span>
              </div>
            </a>

            {/* right col: actions */}
            <div className="flex flex-col items-start md:items-end gap-4 w-full h-full">
              <Button
                variant="secondary"
                onClick={handleDownload}
                className="w-full justify-center"
                disabled={isDownloading}
              >
                {isDownloading ? "Loading..." : "Download Certificate"}
              </Button>
              <div className="flex w-full justify-center md:justify-end">
                <a
                  href={mailtoLink}
                  onClick={() =>
                    track("Report Issue Clicked", {
                      order_id: orderId,
                      certificate_index: certificateIndex,
                    })
                  }
                  className="w-full px-6 py-4 text-center no-underline font-monoBold text-xs uppercase tracking-wider font-bold transition-all duration-200 ease-out disabled:opacity-40 disabled:cursor-not-allowed active:translate-y-0.5 bg-transparent text-collectors-black border border-solid border-collectors-black hover:bg-collectors-black hover:text-white"
                >
                  Report an Issue
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden CertificatePrint component for PDF generation */}
      <div className="fixed -left-[9999px] -top-[9999px] opacity-0 pointer-events-none">
        <CertificatePrint
          cert={processedCert}
          onClose={() => {}}
          printDate={printDate}
          printRef={printCertificateRef}
        />
      </div>
    </div>
  );
}
